import { create } from 'zustand'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { fetchUserProfile, type ProfileStatus } from '@/lib/api'

interface AuthState {
  // State
  user: User | null
  session: Session | null
  loading: boolean
  initialized: boolean
  error: string | null
  // Approval status
  approvalStatus: ProfileStatus | null  // null = not checked yet, 'pending' | 'approved' | 'rejected'
  approvalChecked: boolean  // Whether we've checked the profile status
  emailVerified: boolean  // Whether the user has verified their email via approval link
  // Feature flags
  levineVoiceEnabled: boolean  // Whether user has access to Levine Voice feature

  // Actions
  initialize: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<boolean>
  requestPasswordReset: (email: string) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
  checkApprovalStatus: () => Promise<void>
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Initial state
  user: null,
  session: null,
  loading: false,
  initialized: false,
  error: null,
  approvalStatus: null,
  approvalChecked: false,
  emailVerified: false,
  levineVoiceEnabled: false,

  initialize: async () => {
    // If already initialized, just re-check approval status (it may have changed)
    if (get().initialized) {
      const { user } = get()
      if (user) {
        get().checkApprovalStatus()
      }
      return
    }

    set({ loading: true })

    try {
      // Get current session
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) throw error

      set({
        session,
        user: session?.user ?? null,
        initialized: true,
        loading: false,
      })

      // Check approval status if user is authenticated
      if (session?.user) {
        get().checkApprovalStatus()
      }

      // Set up auth state listener
      supabase.auth.onAuthStateChange((_event, session) => {
        set({
          session,
          user: session?.user ?? null,
        })
        // Check approval status when auth state changes
        if (session?.user) {
          get().checkApprovalStatus()
        } else {
          set({ approvalStatus: null, approvalChecked: false })
        }
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize auth',
        loading: false,
        initialized: true,
      })
    }
  },

  signInWithGoogle: async () => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/z`,
        },
      })

      if (error) throw error
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in',
        loading: false,
      })
    }
  },

  signInWithEmail: async (email: string, password: string) => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in',
        loading: false,
      })
    }
  },

  signUpWithEmail: async (email: string, password: string): Promise<boolean> => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/zinsser`,
        },
      })

      if (error) throw error
      set({ loading: false })
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign up',
        loading: false,
      })
      return false
    }
  },

  requestPasswordReset: async (email: string) => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      })

      if (error) throw error
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to send reset email',
        loading: false,
      })
    }
  },

  signOut: async () => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error

      // CRITICAL: Clear user-specific data from localStorage on sign out
      // This prevents the next user from seeing previous user's data
      localStorage.removeItem('z-store')
      localStorage.removeItem('z-store-user-id')

      // CRITICAL: Also reset the in-memory Zustand state
      // This prevents the persist middleware from writing stale data back to localStorage
      // Dynamic import to avoid circular dependency
      const { useZStore } = await import('./useZStore')
      useZStore.getState().startNewSession()

      set({
        user: null,
        session: null,
        loading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign out',
        loading: false,
      })
    }
  },

  clearError: () => set({ error: null }),

  checkApprovalStatus: async () => {
    const { user } = get()
    if (!user) {
      set({ approvalStatus: null, approvalChecked: true, emailVerified: false, levineVoiceEnabled: false })
      return
    }

    try {
      const profile = await fetchUserProfile()
      if (profile) {
        set({
          approvalStatus: profile.status,
          approvalChecked: true,
          emailVerified: profile.email_verified ?? false,
          levineVoiceEnabled: profile.levine_voice_enabled ?? false,
        })
      } else {
        // Profile doesn't exist yet - treat as pending (will be created by trigger)
        set({ approvalStatus: 'pending', approvalChecked: true, emailVerified: false, levineVoiceEnabled: false })
      }
    } catch (error) {
      console.error('Failed to check approval status:', error)
      // On error, assume pending to be safe
      set({ approvalStatus: 'pending', approvalChecked: true, emailVerified: false, levineVoiceEnabled: false })
    }
  },
}))
